// ─── LiveIconTabs ───────────────────────────────────────────────────────────
// The three-icon strip at the top of the Live section (Data Logger /
// Communications / Sensor Readings).
//
// It lived as a copy-pasted block inside each of the three Live tab files, and
// the copies had already drifted: Data Logger and Communications rendered a
// 96×64 button with a 56px glyph, while Sensor Readings rendered 48×48 with a
// 24px glyph — so the strip visibly changed size when you switched to that tab.
// One component, one size, used by all three.
//
// The glyphs are the XD icons supplied as PNG crops (public/images/icon-font*.png).
// Those crops carried an opaque background and a baked-in colour, so each was
// converted into a transparent mask and painted in both states — grey #AFAFAF
// when idle, accent #358ECD when selected — at 3× the display size so they stay
// sharp on high-DPI screens.
// ────────────────────────────────────────────────────────────────────────────

const TABS = [
  { index: 0, title: 'Data Logger',     file: 'logger' },
  { index: 1, title: 'Communications',  file: 'comms'  },
  { index: 2, title: 'Sensor Readings', file: 'sensor' },
]

// The Iridium Messages tab is appended only for Iridium loggers (device.iridium).
// It has no PNG crop like the other three, so it uses an inline satellite-dish
// glyph painted in the same idle grey / active accent as the others.
const IRIDIUM_TAB = { index: 3, title: 'Iridium Messages', svg: 'satellite' }

const SatelliteGlyph = ({ active }) => (
  <svg
    viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round"
    className={`w-12 h-12 ${active ? 'text-[#358ECD]' : 'text-[#AFAFAF]'}`}
    aria-hidden="true"
  >
    <path d="M4 10a7.31 7.31 0 0 0 10 10Z" />
    <path d="m9 15 3-3" />
    <path d="M17 13a6 6 0 0 0-6-6" />
    <path d="M21 13A10 10 0 0 0 11 3" />
  </svg>
)

// Both state variants are mounted and toggled with `hidden` rather than swapping
// one src: the browser fetches both up front (≈10 KB total), so selecting a tab
// doesn't flash an empty icon while the other file loads.
const Glyph = ({ file, title, active }) => (
  <>
    <img
      src={`/images/live/${file}.png`}
      alt={title}
      className={`max-h-14 max-w-[88px] object-contain ${active ? 'hidden' : ''}`}
    />
    <img
      src={`/images/live/${file}-active.png`}
      alt={title}
      className={`max-h-14 max-w-[88px] object-contain ${active ? '' : 'hidden'}`}
    />
  </>
)

const IconTab = ({ active, onClick, title, children }) => (
  <button
    onClick={onClick}
    title={title}
    aria-label={title}
    aria-pressed={active}
    // Larger than the old buttons (was 96×64 / 48×48) to match the XD sizing.
    className={`flex items-center justify-center w-24 h-16 rounded-lg transition-all ${
      active
        ? 'bg-[#EBF5FB] border-2 border-[#358ECD]'
        : 'bg-white border-2 border-gray-300 hover:border-[#358ECD]'
    }`}
  >
    {children}
  </button>
)

export default function LiveIconTabs({ currentLiveIndex, setCurrentLiveIndex, devices = [], deviceId = null, onSelectDevice, streamSlotRef = null, showIridium = false }) {
  const tabs = showIridium ? [...TABS, IRIDIUM_TAB] : TABS
  return (
    // The icon strip sits on the left; the device switcher ("Data Logger")
    // on the right, so every Live sub-tab (Data Logger / Communications /
    // Sensor Readings) shows the same picker and switches all tabs together.
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <div className="flex gap-3 items-center">
        {tabs.map(({ index, title, file, svg }) => {
          const active = currentLiveIndex === index
          return (
            <IconTab
              key={index}
              active={active}
              onClick={() => setCurrentLiveIndex(index)}
              title={title}
            >
              {svg
                ? <SatelliteGlyph active={active} />
                : <Glyph file={file} title={title} active={active} />}
            </IconTab>
          )
        })}
      </div>
      <div className="flex items-center gap-4 flex-wrap">
        {/* Slot for the Sensor Readings "Start/Stop + Sample #" controls, which
            the Sensor Readings tab portals in here (per the XD it sits left of the
            Data Logger picker). Only rendered on that tab. */}
        {currentLiveIndex === 2 && streamSlotRef && (
          <div ref={streamSlotRef} className="flex items-center gap-3" />
        )}
        {/* Always show the Data Logger picker when the project has any device
            (matches the XD), so the user can see which logger they're viewing and
            switch when there's more than one. */}
        {devices.length >= 1 && onSelectDevice && (
          <div className="flex items-center gap-2">
            <span className="common-size15 text-xs font-semibold text-[#4D4D4D] whitespace-nowrap">Data Logger</span>
            <select
              value={String(deviceId ?? '')}
              onChange={(e) => onSelectDevice(Number(e.target.value))}
              className="brdr-cstm border border-gray-300 rounded-md px-2 py-1.5 text-sm text-gray-700 bg-white focus:outline-none focus:border-[#358ECD] max-w-[240px]"
            >
              {devices.map((d) => (
                <option key={d.id} value={d.id}>{d.label}</option>
              ))}
            </select>
          </div>
        )}
      </div>
    </div>
  )
}
